<template>
    <section>
        <template v-if="!single">
            <div class="is-relative">
                <h3 class="stepper__content__header">{{ $t('ADDRESS_DATA') }}</h3>
            </div>
            <steps :activeStep="2" :steps="2" class="text-xs-center mb-4"></steps>
        </template>
        <div class="content__wrapper">
            <div class="d-flex align-center mb-5">
                <h4 class="stepper__content__title with-description">
                    {{ $t('GIVE_ADDRESS_DATA') }}
                </h4>
                <div class="description box-shadow box-shadow--arrow-left">
                    {{ $t('MEDICAL_CARD_INFO') }}
                </div>
            </div>
            <div class="flex-row row mt-5 mb-3">
                <div class="log-reg-form--register validation-fields-container" novalidate spellcheck="false">
                    <div class="col-xs-12 col-sm-5">
                        <div class="form-group">
                            <input
                                class="text-input -white-input-border"
                                name="street"
                                :placeholder="$t('STREET')"
                                type="text"
                                v-model="address.street"
                                @keyup="inputKeyUp"
                                required
                            />
                        </div>
                    </div>
                    <div class="col-xs-12 col-sm-5 col-sm-offset-1">
                        <div class="row">
                            <div class="col-xs-12 col-sm-6">
                                <div class="form-group">
                                    <input
                                        class="text-input -white-input-border"
                                        name="buildingNumber"
                                        :placeholder="$t('BUILDING_NR')"
                                        type="text"
                                        v-model="address.buildingNumber"
                                        @keyup="inputKeyUp"
                                        required
                                    />
                                </div>
                            </div>
                            <div class="col-xs-12 col-sm-6">
                                <div class="form-group">
                                    <input
                                        class="text-input -white-input-border"
                                        name="flatNumber"
                                        :placeholder="$t('LOCAL_NR')"
                                        type="text"
                                        v-model="address.flatNumber"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-xs-12 col-sm-5">
                        <div class="form-group">
                            <input
                                id=""
                                class="text-input -white-input-border"
                                name="city"
                                :placeholder="$t('CITY')"
                                type="text"
                                v-model="address.city"
                                @keyup="inputKeyUp"
                                required
                            />
                        </div>
                    </div>
                    <div class="col-xs-12 col-sm-5 col-sm-offset-1">
                        <div class="form-group">
                            <input
                                class="text-input -white-input-border"
                                name="zipCode"
                                :placeholder="$t('POSTAL_CODE')"
                                type="text"
                                v-model="address.zipCode"
                                @keyup="inputKeyUp"
                                required
                            />
                        </div>
                    </div>
                    <div class="col-xs-12 col-sm-5">
                        <div class="form-group">
                            <el-select
                                v-model="address.countryCode"
                                filterable
                                :placeholder="$t('COUNTRY')"
                                class="text-input -white-input-border"
                                :class="{ 'invalid-date': address.countryCode === '' && formSubmitted }"
                                :filter-method="filterOptions"
                            >
                                <el-option
                                    v-for="item in filteredCountries"
                                    :key="item.id"
                                    :label="`${item.name} (${item.nativeName})`"
                                    :value="item.code"
                                />
                            </el-select>
                        </div>
                    </div>
                    <button
                        id="formBtn"
                        class="buttons-container__button validate-test validate-button hidden"
                    ></button>
                </div>
            </div>
            <label v-if="address.countryCode === 'IE'" class="checkbox -checkbox-long" style="max-width: 100%">
                <input v-model="address.irelandAgreement" class="checkbox__check-input" required type="checkbox" />
                <span class="checkbox__check -grey-border-checkbox"></span>
                <span class="checkbox__text -black-checkbox-text" style="font-size: 14px;font-weight: 400">
                    I have read and understand the information made available by the service provider Braster S.A.
                    regarding the rendering of services with the use of telemedicine. All of my questions have been
                    answered to my satisfaction. I hereby give my informed consent for the use of telemedicine for the
                    purpose of rendering the thermographic tests by the service provider.
                </span>
            </label>
            <div class="text-center">
                <div v-if="single">
                    <button @click="$emit('cancel')" class="w160 mr-3 app-btn app-btn--small app-btn-default mt-3">
                        {{ $t('CANCEL') }}
                    </button>
                    <button @click="save" class="w160 mr-3 app-btn app-btn--small app-btn-primary mt-3">
                        {{ $t('SAVE') }}
                    </button>
                </div>
                <div v-else>
                    <button @click="goBack" class="w160 mr-3 app-btn app-btn--small app-btn-default mt-3">
                        {{ $t('PREVIOUS') }}
                    </button>
                    <button @click="goNext" class="w160 mr-3 app-btn app-btn--small app-btn-primary mt-3">
                        {{ $t('NEXT') }}
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { validate } from './../../../validation';
import Steps from './helpers/steps.vue';
import { mapState } from 'vuex';
import { alphabeticSortByName } from '../../../utils';

const defaultAddress = {
    irelandAgreement: false,
    street: '',
    buildingNumber: '',
    flatNumber: '',
    city: '',
    zipCode: '',
    countryCode: '',
};

export default {
    name: 'survey-step5-q2',
    props: {
        single: Boolean,
    },
    data() {
        return {
            address: { ...defaultAddress },
            formSubmitted: false,
            filteredCountries: null,
        };
    },
    computed: {
        ...mapState(['user']),
        irelandAgreementValid() {
            return (
                this.address.countryCode !== 'IE' ||
                (this.address.countryCode === 'IE' && this.address.irelandAgreement)
            );
        },
    },
    asyncComputed: {
        countries() {
            return this.$http
                .get('/user/public/registration/countries')
                .then(({ data }) => data.sort(alphabeticSortByName));
        },
        filteredCountries() {
            this.filteredCountries = this.countries;
        },
    },
    methods: {
        performValidation() {
            return validate($('#formBtn')) && this.address.countryCode !== '' && this.irelandAgreementValid;
        },
        inputKeyUp() {
            if (this.formSubmitted) {
                this.performValidation();
            }
        },
        goBack() {
            this.$store.commit('STATE_SURVEY', {
                section5: {
                    active_section: 1,
                },
                address: { ...this.address },
            });
        },
        goNext() {
            this.formSubmitted = true;
            if (this.performValidation()) {
                this.save();
                this.$http.get('/openehr/api/composition/').then(({ data }) => {
                    this.$store.state.medicalData = data;
                    if (this.address.countryCode !== 'IE') {
                        this.$store.commit('STATE_SURVEY', {
                            activeStepper: 3,
                            section5: {
                                active_section: 1,
                            },
                        });
                    } else {
                        this.$store.commit('STATE_SURVEY', {
                            section5: {
                                active_section: 3,
                            },
                        });
                    }
                });
            }
        },
        save() {
            this.formSubmitted = true;
            if (this.performValidation()) {
                this.$http.patch('/user/api/profile/user/address', this.address).then(() => this.$emit('save'));
            }
        },
        filterOptions(filterValue) {
            let filteredCountries = this.countries.filter(country => {
                return country.name.toLowerCase().startsWith(filterValue.toLowerCase());
            });

            if (filterValue === null) {
                this.filteredCountries = this.countries;
            } else {
                this.filteredCountries = filteredCountries;
            }
        },
    },
    created() {
        const irelandAgreement = this.user.userAgreements.some(
            ua => ua.agreement.label === 'TELEMEDICINE_SERVICES' && ua.agreed,
        );
        const userAddress = this.user.address;
        const surveyAddress = this.$store.getters.survey.address;
        this.address = userAddress
            ? { ...userAddress, countryCode: userAddress.country.code, irelandAgreement }
            : userAddress
            ? { ...surveyAddress }
            : { ...defaultAddress };
    },
    components: {
        Steps,
    },
};
</script>



// WEBPACK FOOTER //
// survey-step5-q2.vue?1a66e6ca